import type { SeededRandom } from './rng.js';
import { angleTo, turnDir } from './math.js';
import {
  EMPTY_INPUT,
  PILOT_STATS,
  applyInput,
  type Controller,
  type PlaneInput,
  type Side,
  type SimState,
} from './sim.js';
export class BotController implements Controller {
  constructor(private readonly pilotConfig: 0 | 1 | 2 | 3) {}
  act(state: SimState, side: Side, rng: SeededRandom): void {
    this.decide(state, side, rng, (input) => applyInput(state, side, input));
  }
  poll(state: SimState, side: Side, rng: SeededRandom): PlaneInput {
    const copy = JSON.parse(JSON.stringify(state)) as SimState,
      result = { ...EMPTY_INPUT };
    this.decide(copy, side, rng, (input) => {
      for (const key of Object.keys(input) as (keyof PlaneInput)[])
        if (input[key]) result[key] = true;
      applyInput(copy, side, input);
    });
    return result;
  }
  private decide(
    state: SimState,
    side: Side,
    rng: SeededRandom,
    apply: (input: PlaneInput) => void,
  ): void {
    const p = state.planes[side],
      foe = state.planes[(1 - side) as Side],
      stats = PILOT_STATS[this.pilotConfig]!;
    const action = (key: keyof PlaneInput): void => apply({ ...EMPTY_INPUT, [key]: true });
    const turn = (dir: number): void => {
      if (dir === 2) action('left');
      else if (dir === 3) action('right');
    };
    if (p.a === 0) {
      action('climb');
      if (p.g >= 250 && p.f < 2) turn(3);
    } else if (p.a === 1) {
      if (p.g < 250) action('climb');
      let dodged = false;
      if (
        p.e > 182 - stats.ah ||
        (p.d > 109 - stats.ah && p.d < 145 + stats.ah && p.e > 168 - stats.ah)
      ) {
        if (stats.am === 0 || rng.below(stats.am) === 0) {
          if (p.f < 4) turn(3);
          else if (p.f > 4 && p.f < 12) turn(2);
          else if (p.f > 11) turn(3);
          dodged = true;
        }
      }
      if (p.e < stats.al) {
        if (p.f < 5) turn(2);
        else if (p.f > 4 && p.f < 9) turn(3);
        dodged = true;
      }
      if (!dodged && (stats.ag === 0 || rng.below(stats.ag) === 0)) {
        const target = angleTo(
          p.x,
          p.y,
          foe.v === 0 ? foe.x : foe.pilotX,
          foe.v === 0 ? foe.y : foe.pilotY,
        );
        const dir = turnDir(p.h, target);
        if (dir >= 2) turn(dir);
        else action('fire');
      }
      if (p.v === 0 && p.hitP === 2 && foe.active) {
        for (let i = 1; i >= 0; i--) {
          const b = state.bullets[(1 - side) * 2 + i]!;
          if (b.y === 255) continue;
          const dx = Math.abs(b.x - p.d),
            dy = Math.abs(b.y - p.e);
          if (dx * dx + dy * dy < 80 + stats.ai) p.evade = true;
        }
      }
      if (p.evade && (p.f > 12 || p.f < 4)) action('eject');
    }
    if (p.v === 4) turn(p.pilotX >> 6 < 127 ? 3 : 2);
    else if (p.v === 1) {
      if (rng.below(stats.aj) === 0) action('eject');
    } else if (p.v === 2) {
      if (p.t >= 2) turn(p.t);
      if (rng.below(stats.ak) === 0) p.t = rng.below(4);
    }
  }
}
